import * as Dialog from "@radix-ui/react-dialog";
import clsx from "clsx";
import { Command } from "cmdk";
import {
  ArrowRight,
  BarChart3,
  ClipboardList,
  Clock,
  FileText,
  FlaskConical,
  GraduationCap,
  Home,
  Paperclip,
  Search,
  SquarePlay,
} from "lucide-react";
import {
  type KeyboardEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "@/common/hooks/useAuth";
import { useCycles } from "@/common/services/cycles/cycles.queries";
import { useManualTestCases } from "@/common/services/scenarios";
import { formatEntityId } from "@/common/utils/entityId";
import { Theme } from "@/compat/radix-themes";

/**
 * Public type for a recently selected command palette item.
 * Persisted in localStorage so the user sees their most recent jumps.
 */
export interface RecentItem {
  /** Stable id, e.g. `cycle-123`, `case-789`, `action-dashboard` */
  id: string;
  /** Short label rendered as the row title */
  label: string;
  /** Optional secondary text (badge or path) */
  detail?: string;
  /** Route the palette should navigate to when this item is chosen */
  href: string;
  /** Section the item belongs to — drives the icon */
  kind: "cycle" | "case" | "action";
}

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Optional initial query injected by `openPalette(initialQuery)` */
  initialQuery?: string;
}

const MAX_RECENTS = 5;
const DEBOUNCE_MS = 250;
const PAGE_SIZE = 8;

const recentsKey = (userId: string | number | undefined): string =>
  `cmdk:recent:${userId ?? "anon"}`;

const readRecents = (userId: string | number | undefined): RecentItem[] => {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(recentsKey(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((p): p is RecentItem => {
      if (!p || typeof p !== "object") return false;
      const candidate = p as Partial<RecentItem>;
      return (
        typeof candidate.id === "string" &&
        typeof candidate.label === "string" &&
        typeof candidate.href === "string" &&
        typeof candidate.kind === "string"
      );
    });
  } catch {
    return [];
  }
};

const writeRecents = (
  userId: string | number | undefined,
  items: RecentItem[],
): void => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      recentsKey(userId),
      JSON.stringify(items.slice(0, MAX_RECENTS)),
    );
  } catch {
    // ignore quota errors
  }
};

const iconForKind = (kind: RecentItem["kind"]): ReactNode => {
  switch (kind) {
    case "cycle":
      return <ClipboardList size={16} aria-hidden="true" />;
    case "case":
      return <FlaskConical size={16} aria-hidden="true" />;
    case "action":
    default:
      return <ArrowRight size={16} aria-hidden="true" />;
  }
};

/**
 * Single row inside the palette. Fixed 40px height to match other 2026-card lists.
 */
interface PaletteRowProps {
  value: string;
  label: string;
  detail?: string;
  icon: ReactNode;
  onSelect: () => void;
  keywords?: string[];
}

const PaletteRow = ({
  value,
  label,
  detail,
  icon,
  onSelect,
  keywords,
}: PaletteRowProps) => (
  <Command.Item
    value={value}
    keywords={keywords}
    onSelect={onSelect}
    className={clsx(
      "group flex h-10 cursor-pointer items-center gap-3 rounded-lg px-3 text-sm",
      "text-ink-strong",
      "data-[selected=true]:bg-brand-50 data-[selected=true]:text-brand-700",
      "transition-colors duration-150",
    )}
  >
    <span className="flex h-6 w-6 shrink-0 items-center justify-center text-ink-mid group-data-[selected=true]:text-brand-600">
      {icon}
    </span>
    <span className="flex-1 truncate">{label}</span>
    {detail && (
      <span className="ml-2 shrink-0 truncate font-mono text-[11px] text-ink-soft group-data-[selected=true]:text-brand-600">
        {detail}
      </span>
    )}
  </Command.Item>
);

interface QuickAction {
  id: string;
  label: string;
  href: string;
  icon: ReactNode;
}

const QUICK_ACTIONS: ReadonlyArray<QuickAction> = [
  {
    id: "action-dashboard",
    label: "Go to Dashboard",
    href: "/",
    icon: <Home size={16} aria-hidden="true" />,
  },
  {
    id: "action-test-cycles",
    label: "Go to Test Cycles",
    href: "/cycles",
    icon: <ClipboardList size={16} aria-hidden="true" />,
  },
  {
    id: "action-test-runs",
    label: "Go to Test Runs",
    href: "/test-management/test-runs",
    icon: <SquarePlay size={16} aria-hidden="true" />,
  },
  {
    id: "action-files",
    label: "Open Upload",
    href: "/uploads",
    icon: <Paperclip size={16} aria-hidden="true" />,
  },
  {
    id: "action-reports",
    label: "Open Reports",
    href: "/reports",
    icon: <BarChart3 size={16} aria-hidden="true" />,
  },
  {
    id: "action-guideme",
    label: "Open Guide Me",
    href: "/guideme",
    icon: <GraduationCap size={16} aria-hidden="true" />,
  },
];

const CommandPalette = ({
  open,
  onOpenChange,
  initialQuery,
}: CommandPaletteProps) => {
  const navigate = useNavigate();
  const { user, userPreferenceData } = useAuth();
  const versionId = userPreferenceData?.data?.versionId as number | undefined;
  const userId = user?.id;

  const [input, setInput] = useState<string>(initialQuery ?? "");
  const [debouncedQuery, setDebouncedQuery] = useState<string>(
    initialQuery ?? "",
  );
  const [recents, setRecents] = useState<RecentItem[]>(() =>
    readRecents(userId),
  );
  const debounceRef = useRef<number | null>(null);

  // Sync recents whenever the user (or open state) changes.
  useEffect(() => {
    setRecents(readRecents(userId));
  }, [userId, open]);

  // When the palette is opened, reset query to whatever the caller passed (or blank).
  useEffect(() => {
    if (open) {
      setInput(initialQuery ?? "");
      setDebouncedQuery(initialQuery ?? "");
    }
  }, [open, initialQuery]);

  // Debounce the typed query (250 ms) so we don't hammer the API on every keystroke.
  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      setDebouncedQuery(input.trim());
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [input]);

  const hasQuery = debouncedQuery.length > 0;

  // ── Data fetching (only when palette is open & user has typed) ────────────
  const cycleQuery = useCycles(
    {
      workspaceVersionId: versionId ?? undefined,
      ...(hasQuery ? { query: debouncedQuery } : {}),
      size: PAGE_SIZE,
      sort: "id,desc",
    },
    open && !!versionId,
  );

  const caseQuery = useManualTestCases({
    versionId: versionId ?? 0,
    size: PAGE_SIZE,
    sort: "name,asc",
    query: hasQuery
      ? `workspaceVersionId:${versionId},name:*${debouncedQuery}*`
      : undefined,
  });

  const cycles = useMemo(() => {
    return (cycleQuery.data?.content ?? []).slice(0, PAGE_SIZE);
  }, [cycleQuery.data]);

  const cases = useMemo(() => {
    const pages =
      (caseQuery.data?.pages as
        | Array<{ data?: { content?: unknown[] } }>
        | undefined) ?? [];
    return pages
      .flatMap(
        (p) => (p?.data?.content as Array<{ id: number; name: string }>) ?? [],
      )
      .slice(0, PAGE_SIZE);
  }, [caseQuery.data]);

  // ── Selection handlers ────────────────────────────────────────────────────
  const recordRecent = useCallback(
    (item: RecentItem) => {
      const filtered = recents.filter((r) => r.id !== item.id);
      const next = [item, ...filtered].slice(0, MAX_RECENTS);
      setRecents(next);
      writeRecents(userId, next);
    },
    [recents, userId],
  );

  const handleSelect = useCallback(
    (item: RecentItem) => {
      recordRecent(item);
      onOpenChange(false);
      navigate(item.href);
    },
    [navigate, onOpenChange, recordRecent],
  );

  // ── ESC handled by Radix Dialog — but cmdk also intercepts; let it bubble.
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") onOpenChange(false);
  };

  const showRecents = !hasQuery && recents.length > 0;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Theme>
          <Dialog.Overlay
            className={clsx(
              "fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm",
              "data-[state=open]:animate-overlay-in data-[state=closed]:animate-overlay-out",
            )}
          />
          <Dialog.Content
            aria-label="Command palette"
            className={clsx(
              "fixed left-1/2 top-[20%] z-[70] w-[calc(100vw-2rem)] max-w-[640px] -translate-x-1/2",
              "rounded-2xl bg-white shadow-2xl ring-1 ring-black/5",
              "focus:outline-none overflow-hidden",
              "data-[state=open]:animate-dialog-in data-[state=closed]:animate-dialog-out",
            )}
            onOpenAutoFocus={(e) => {
              // Let cmdk's input claim focus instead of the dialog wrapper.
              e.preventDefault();
            }}
          >
            <Dialog.Title className="sr-only">Command palette</Dialog.Title>
            <Dialog.Description className="sr-only">
              Search test cycles, cases or jump to a page.
            </Dialog.Description>
            <Command
              label="Command palette"
              loop
              onKeyDown={handleKeyDown}
              className="flex flex-col"
            >
              {/* Search input row */}
              <div className="flex items-center gap-2 border-b border-ink-line px-4 py-3">
                <Search size={16} className="text-ink-mid" aria-hidden="true" />
                <Command.Input
                  value={input}
                  onValueChange={setInput}
                  autoFocus
                  placeholder="Search cycles, test cases…"
                  className={clsx(
                    "flex-1 bg-transparent text-[14px] text-ink-strong placeholder:text-ink-soft",
                    "outline-none focus:ring-0",
                  )}
                />
                <kbd
                  className="hidden h-5 select-none items-center rounded border border-ink-line px-1.5 font-mono text-[10px] text-ink-mid sm:inline-flex"
                  aria-hidden="true"
                >
                  ESC
                </kbd>
              </div>

              {/* Results list */}
              <Command.List className="max-h-[420px] overflow-y-auto p-2">
                <Command.Empty className="px-3 py-6 text-center text-xs text-ink-soft">
                  No matches found.
                </Command.Empty>

                {/* Quick actions — always at top */}
                <Command.Group
                  heading="Quick actions"
                  className={clsx(
                    "[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-2",
                    "[&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-semibold",
                    "[&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider",
                    "[&_[cmdk-group-heading]]:text-ink-soft",
                  )}
                >
                  {QUICK_ACTIONS.map((action) => (
                    <PaletteRow
                      key={action.id}
                      value={`action ${action.label}`}
                      label={action.label}
                      icon={action.icon}
                      onSelect={() =>
                        handleSelect({
                          id: action.id,
                          label: action.label,
                          href: action.href,
                          kind: "action",
                        })
                      }
                    />
                  ))}
                </Command.Group>

                {showRecents && (
                  <>
                    <Command.Separator className="my-2 h-px bg-ink-line" />
                    <Command.Group
                      heading="Recent"
                      className={clsx(
                        "[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-2",
                        "[&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-semibold",
                        "[&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider",
                        "[&_[cmdk-group-heading]]:text-ink-soft",
                      )}
                    >
                      {recents.map((item) => (
                        <PaletteRow
                          key={`recent-${item.id}`}
                          value={`recent ${item.label}`}
                          label={item.label}
                          detail={item.detail}
                          icon={
                            <span className="flex items-center gap-1 text-ink-mid">
                              <Clock size={14} aria-hidden="true" />
                            </span>
                          }
                          onSelect={() => handleSelect(item)}
                        />
                      ))}
                    </Command.Group>
                  </>
                )}

                {(cycles.length > 0 || cycleQuery.isFetching) && (
                  <>
                    <Command.Separator className="my-2 h-px bg-ink-line" />
                    <Command.Group
                      heading="Test Cycles"
                      className={clsx(
                        "[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-2",
                        "[&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-semibold",
                        "[&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider",
                        "[&_[cmdk-group-heading]]:text-ink-soft",
                      )}
                    >
                      {cycles.map((cycle) => {
                        const cycleId = String(cycle.id);
                        const idBadge = formatEntityId("cycle", cycle.id);
                        return (
                          <PaletteRow
                            key={`cycle-${cycleId}`}
                            value={`cycle ${cycle.name} ${idBadge}`}
                            label={cycle.name || "Untitled cycle"}
                            detail={idBadge}
                            icon={iconForKind("cycle")}
                            keywords={[idBadge, cycleId]}
                            onSelect={() =>
                              handleSelect({
                                id: `cycle-${cycleId}`,
                                label: cycle.name || "Untitled cycle",
                                detail: idBadge,
                                href: `/cycles/${cycleId}/results`,
                                kind: "cycle",
                              })
                            }
                          />
                        );
                      })}
                    </Command.Group>
                  </>
                )}

                {(cases.length > 0 || caseQuery.isFetching) && (
                  <>
                    <Command.Separator className="my-2 h-px bg-ink-line" />
                    <Command.Group
                      heading="Test Cases"
                      className={clsx(
                        "[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-2",
                        "[&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-semibold",
                        "[&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider",
                        "[&_[cmdk-group-heading]]:text-ink-soft",
                      )}
                    >
                      {cases.map((tc) => {
                        const caseId = String(tc.id);
                        const idBadge = `TC-${caseId.padStart(4, "0")}`;
                        return (
                          <PaletteRow
                            key={`case-${caseId}`}
                            value={`case ${tc.name} ${idBadge}`}
                            label={tc.name || "Untitled case"}
                            detail={idBadge}
                            icon={iconForKind("case")}
                            keywords={[idBadge, caseId]}
                            onSelect={() =>
                              handleSelect({
                                id: `case-${caseId}`,
                                label: tc.name || "Untitled case",
                                detail: idBadge,
                                href: `/testcases/${caseId}`,
                                kind: "case",
                              })
                            }
                          />
                        );
                      })}
                    </Command.Group>
                  </>
                )}
              </Command.List>

              {/* Footer hint */}
              <div className="flex items-center justify-between border-t border-ink-line px-4 py-2 text-[11px] text-ink-soft">
                <span className="flex items-center gap-2">
                  <FileText size={12} aria-hidden="true" />
                  <span>Search anywhere</span>
                </span>
                <span className="flex items-center gap-2">
                  <kbd className="rounded border border-ink-line px-1 font-mono">
                    ↑↓
                  </kbd>
                  <span>navigate</span>
                  <kbd className="ml-2 rounded border border-ink-line px-1 font-mono">
                    ↵
                  </kbd>
                  <span>open</span>
                </span>
              </div>
            </Command>
          </Dialog.Content>
        </Theme>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default CommandPalette;
